import { useMemo, useState, type ReactNode } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Zap,
  Target as TargetIcon,
  PlayCircle,
  AlertOctagon,
  CalendarClock,
  PlaySquare,
  Power,
  TrendingUp,
  TrendingDown,
  Download,
  ArrowRight,
  ShieldAlert,
} from 'lucide-react';
import { AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { assetGroupsApi, engagementsApi, findingsApi, runsApi, killSwitchApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LoadingState, ErrorState, EmptyState } from '../components/ui/states';
import { RUN_STATUS_VARIANT } from '../lib/run-status';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import { useTheme } from '../lib/theme';
import { SEVERITY_COLOR_BY_THEME, BRAND_COLOR_BY_THEME, CHART_NEUTRAL_BY_THEME } from '../lib/chart-colors';
import {
  SEVERITY_ORDER,
  severityBreakdown,
  findingsTrend,
  computeSecurityPosture,
  topAttackTargets,
  targetsAddedSince,
  runsStartedSince,
  findingsDiscoveredSince,
  completedRunsSince,
} from '../lib/dashboard-analytics';
import type { FindingRecord, Run, Severity } from '../lib/types';

const ACTIVE_RUN_STATUSES: Run['status'][] = ['queued', 'discovery', 'validation', 'reporting'];
const RECENT_SCANS_LIMIT = 8;
const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
] as const;

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Informational',
};

/**
 * Severity tint for the "open findings" stat card. Critical outranks high — a card is never
 * both red and orange, only one tint applies. `bg-brand/10` resolves to a 10%-opacity tint of
 * BUI's brand orange (tailwind.config.js `brand.DEFAULT`) — used here precisely because the spec
 * calls for that exact colour, not an arbitrary orange.
 */
function findingsTone(criticalCount: number, highCount: number): string | undefined {
  if (criticalCount > 0) return 'bg-danger/10';
  if (highCount > 0) return 'bg-brand/10';
  return 'bg-success/10';
}

interface Trend {
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
}

function TrendLine({ trend }: { trend?: Trend }) {
  if (!trend) return null;
  const toneClass = { success: 'text-success', warning: 'text-warning', danger: 'text-danger', neutral: 'text-ink-faint' }[trend.tone];
  const Icon = trend.tone === 'danger' || trend.tone === 'warning' ? TrendingUp : trend.tone === 'success' ? TrendingUp : TrendingDown;
  return (
    <p className={`mt-1 flex items-center gap-1 text-xs font-medium ${toneClass}`}>
      <Icon size={12} />
      {trend.label}
    </p>
  );
}

function StatCard({ icon, label, value, tone, trend }: { icon: ReactNode; label: string; value: ReactNode; tone?: string; trend?: Trend }) {
  return (
    <Card className={tone}>
      <CardContent className="flex items-start gap-4 pt-6">
        <div className="rounded-full bg-brand-subtle p-2.5 text-brand-hover">{icon}</div>
        <div className="min-w-0">
          <p className="text-sm text-ink-muted">{label}</p>
          <p className="truncate text-card-value font-bold text-ink">{value}</p>
          <TrendLine trend={trend} />
        </div>
      </CardContent>
    </Card>
  );
}

/** Findings-by-severity donut — Recharts PieChart with a centre total label and a legend showing
 * counts + percentages, theme-aware colours (BUI orange only where it's the largest slice, per the
 * brief — severity colour otherwise). */
function SeverityDonut({ counts }: { counts: Record<Severity, number> }) {
  const { resolved } = useTheme();
  const severityColor = SEVERITY_COLOR_BY_THEME[resolved];
  const brandColor = BRAND_COLOR_BY_THEME[resolved];
  const total = SEVERITY_ORDER.reduce((sum, s) => sum + counts[s], 0);
  const present = SEVERITY_ORDER.filter((s) => counts[s] > 0);
  // The single largest segment (first in severity-priority order on a tie) uses BUI orange as the
  // "primary series" accent (brief Section 20); every other segment keeps its own severity colour.
  const largestSeverity = present.reduce<Severity | null>((champion, s) => (champion === null || counts[s] > counts[champion] ? s : champion), null);
  const data = present.map((s) => ({ severity: s, value: counts[s] }));

  if (total === 0) {
    return <EmptyState icon={<ShieldAlert size={28} />} title="No open findings" description="Nothing to show here yet." />;
  }

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
      <div className="relative h-48 w-48 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="severity" innerRadius="65%" outerRadius="100%" paddingAngle={2} stroke="none">
              {data.map((d) => (
                <Cell key={d.severity} fill={d.severity === largestSeverity ? brandColor : severityColor[d.severity]} />
              ))}
            </Pie>
            <RechartsTooltip
              formatter={(value: number, _name, entry) => [`${value} (${Math.round((value / total) * 100)}%)`, SEVERITY_LABEL[entry.payload.severity as Severity]]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-card-value font-bold text-ink">{total}</span>
          <span className="text-xs text-ink-muted">Total</span>
        </div>
      </div>
      <ul className="w-full space-y-1.5 text-sm">
        {present.map((s) => (
          <li key={s} className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-ink-muted">
              <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: s === largestSeverity ? brandColor : severityColor[s] }} />
              {SEVERITY_LABEL[s]}
            </span>
            <span className="font-medium text-ink">
              {counts[s]} ({Math.round((counts[s] / total) * 100)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FindingsTrendChart({ points }: { points: { date: string; label: string; count: number }[] }) {
  const { resolved } = useTheme();
  const brandColor = BRAND_COLOR_BY_THEME[resolved];
  const neutral = CHART_NEUTRAL_BY_THEME[resolved];
  const total = points.reduce((sum, p) => sum + p.count, 0);

  if (total === 0) {
    return <EmptyState icon={<AlertOctagon size={28} />} title="No findings in this range" description="Nothing discovered in the selected window." />;
  }

  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="findings-trend-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={brandColor} stopOpacity={0.28} />
              <stop offset="100%" stopColor={brandColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={neutral.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: neutral.axis, fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: neutral.grid }}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis tick={{ fill: neutral.axis, fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
          <RechartsTooltip
            contentStyle={{ background: neutral.surface, border: `1px solid ${neutral.grid}`, borderRadius: 10, fontSize: 12 }}
            labelStyle={{ color: neutral.axis }}
          />
          <Area type="monotone" dataKey="count" name="Findings" stroke={brandColor} strokeWidth={2} fill="url(#findings-trend-fill)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function TopAttackPathsPanel({ targets }: { targets: ReturnType<typeof topAttackTargets> }) {
  if (targets.length === 0) {
    return <EmptyState icon={<ShieldAlert size={28} />} title="No targeted assets yet" description="Risk ranking appears once findings exist." />;
  }
  return (
    <ul className="space-y-1">
      {targets.map((t) => (
        <li key={t.target} className="flex items-center justify-between gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-surface">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-ink">{t.target}</p>
            <p className="text-xs text-ink-muted">
              {t.count} finding{t.count === 1 ? '' : 's'}
            </p>
          </div>
          <Badge variant={t.tone === 'danger' ? 'danger' : t.tone === 'warning' ? 'warning' : 'neutral'} className="shrink-0 tabular-nums">
            {t.riskScore}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

/** Semicircular 0-100 gauge — hand-rolled SVG rather than forcing Recharts' PieChart into a gauge
 * shape (no native gauge chart type exists in Recharts, and a 180/0-degree Pie hack is harder to
 * label precisely). `pathLength=100` lets the fill arc use a plain 0-100 stroke-dash percentage. */
function SecurityPostureGauge({ posture, trend }: { posture: ReturnType<typeof computeSecurityPosture>; trend?: Trend }) {
  const { resolved } = useTheme();
  const neutral = CHART_NEUTRAL_BY_THEME[resolved];
  const toneColor = { success: '#35c98a', warning: '#f0b54a', danger: '#f16d6d' }[posture.tone];
  const arcPath = 'M 20 100 A 80 80 0 0 1 180 100';

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 110" className="w-full max-w-[240px]" role="img" aria-label={`Security posture score ${posture.score}, ${posture.label}`}>
        <path d={arcPath} fill="none" stroke={neutral.grid} strokeWidth="14" strokeLinecap="round" pathLength={100} />
        <path
          d={arcPath}
          fill="none"
          stroke={toneColor}
          strokeWidth="14"
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray={100}
          strokeDashoffset={100 - posture.score}
        />
      </svg>
      <div className="-mt-8 flex flex-col items-center">
        <span className="text-display font-bold text-ink">{posture.score}</span>
        <span className={`text-sm font-semibold ${{ success: 'text-success', warning: 'text-warning', danger: 'text-danger' }[posture.tone]}`}>
          {posture.label}
        </span>
        <TrendLine trend={trend} />
      </div>
    </div>
  );
}

export function Dashboard() {
  const { principal } = useAuth();
  const canQuickScan = principal?.role === undefined || can(principal.role, 'authorization:activate');
  const canReadKillSwitch = principal?.role === undefined || can(principal.role, 'killSwitch:read');
  const [rangeDays, setRangeDays] = useState<'7' | '30' | '90'>('30');

  const killSwitchQuery = useQuery({
    queryKey: ['kill-switch'],
    queryFn: () => killSwitchApi.get(),
    enabled: canReadKillSwitch,
  });

  const assetGroupsQuery = useQuery({ queryKey: ['asset-groups'], queryFn: () => assetGroupsApi.list() });
  const assetGroups = useMemo(() => assetGroupsQuery.data?.assetGroups ?? [], [assetGroupsQuery.data]);

  const targetQueries = useQueries({
    queries: assetGroups.map((g) => ({
      queryKey: ['targets', g.id],
      queryFn: () => assetGroupsApi.targets(g.id),
      enabled: assetGroups.length > 0,
    })),
  });

  const engagementsQuery = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list() });
  const engagements = useMemo(() => engagementsQuery.data?.engagements ?? [], [engagementsQuery.data]);

  const runQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['runs', e.id],
      queryFn: () => runsApi.list(e.id),
      enabled: engagements.length > 0,
    })),
  });

  const findingQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['dashboard-findings', e.id],
      queryFn: () => findingsApi.forEngagement(e.id),
      enabled: engagements.length > 0,
    })),
  });

  const isLoading = assetGroupsQuery.isLoading || engagementsQuery.isLoading;
  const firstError = assetGroupsQuery.error ?? engagementsQuery.error;
  if (isLoading) return <LoadingState />;
  if (assetGroupsQuery.isError || engagementsQuery.isError) {
    return <ErrorState error={firstError} onRetry={() => { assetGroupsQuery.refetch(); engagementsQuery.refetch(); }} />;
  }

  const allTargets = targetQueries.flatMap((q) => q.data?.targets ?? []);
  const totalTargets = allTargets.length;

  const runsWithEngagement = engagements.flatMap((engagement, i) =>
    (runQueries[i]?.data?.runs ?? []).map((run) => ({ engagement, run })),
  );
  const allRuns = runsWithEngagement.map(({ run }) => run);
  const activeScans = runsWithEngagement.filter(({ run }) => ACTIVE_RUN_STATUSES.includes(run.status)).length;

  const allFindings: FindingRecord[] = findingQueries.flatMap((q) => Object.values(q.data?.byZone ?? {}).flat());
  const openCritical = allFindings.filter((f) => f.status === 'open' && f.severity === 'critical').length;
  const openHigh = allFindings.filter((f) => f.status === 'open' && f.severity === 'high').length;
  const openCritHigh = openCritical + openHigh;

  const findingCountByRunId = new Map<string, number>();
  for (const f of allFindings) {
    if (!f.runId) continue;
    findingCountByRunId.set(f.runId, (findingCountByRunId.get(f.runId) ?? 0) + 1);
  }

  const recentScans = [...runsWithEngagement].sort((a, b) => new Date(b.run.createdAt).getTime() - new Date(a.run.createdAt).getTime());
  // "Last scan" means the last scan that finished. The `Run` type has no `completedAt` field —
  // it doesn't exist anywhere in the backend (schema/repo/API all stop at createdAt/updatedAt) —
  // so `updatedAt` on a `completed` run is the closest available proxy, since it's bumped exactly
  // when the run transitions into that terminal status. Fall back to the most recent run's
  // createdAt (i.e. "still in progress") when nothing has completed yet.
  const completedScans = [...runsWithEngagement]
    .filter(({ run }) => run.status === 'completed')
    .sort((a, b) => new Date(b.run.updatedAt).getTime() - new Date(a.run.updatedAt).getTime());
  const lastScan = completedScans[0]?.run.updatedAt ?? recentScans[0]?.run.createdAt;

  const nothingSetUpYet = assetGroups.length === 0 && engagements.length === 0;

  const newScanButton = canQuickScan ? (
    <Button asChild size="lg" className="gap-2 bg-brand text-white hover:bg-brand-hover">
      <Link to="/quick-scan">
        <Zap size={18} />
        New Scan
      </Link>
    </Button>
  ) : null;

  if (nothingSetUpYet) {
    return (
      <div>
        <PageHeader title="Dashboard" description="Welcome to DefendIQ." />
        <EmptyState
          icon={<PlaySquare size={32} />}
          title="Run your first scan"
          description={
            canQuickScan
              ? 'Quick Scan walks you through verifying a target, authorizing it, and starting a test — in a few minutes.'
              : "Nothing's been scanned yet. Ask a workspace Admin or Owner to run the first scan."
          }
          action={
            canQuickScan ? (
              <Button asChild>
                <Link to="/quick-scan">
                  <Zap size={16} /> Start your first scan
                </Link>
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }

  const killSwitchState = killSwitchQuery.data;
  const now = new Date();
  const rangeNum = Number(rangeDays);
  const rangeStart = new Date(now.getTime() - rangeNum * 24 * 60 * 60 * 1000);
  const startOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  const severityCounts = severityBreakdown(allFindings);
  const trendPoints = findingsTrend(allFindings, rangeNum, now);
  const posture = computeSecurityPosture(severityCounts);
  const rankedTargets = topAttackTargets(allFindings, 5);

  const newTargets = targetsAddedSince(allTargets, rangeStart);
  const scansToday = runsStartedSince(allRuns, startOfToday);
  const newCritHigh = findingsDiscoveredSince(allFindings, rangeStart, ['critical', 'high']);
  const completedInRange = completedRunsSince(allRuns, rangeStart);

  const exportRecentScansCsv = () => {
    const header = ['Target', 'Status', 'Started', 'Findings'];
    const rows = recentScans
      .slice(0, RECENT_SCANS_LIMIT)
      .map(({ engagement, run }) => [engagement.name, run.status, run.createdAt, String(findingCountByRunId.get(run.id) ?? 0)]);
    const csv = [header, ...rows].map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `defendiq-recent-scans-${now.toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Compact operational summary (brief Section 13) — replaces the old oversized, mostly
          empty banner. Capped well under the 220px desktop guideline; every indicator here is real
          data — killSwitchState/totalTargets, omitted entirely rather than guessed when a viewer's
          role can't read the kill switch. */}
      <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-border bg-surface-raised px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand">Controlled testing</p>
          <h1 className="mt-1 text-section-title font-semibold text-ink">Launch an authorised assessment</h1>
          <p className="mt-1 max-w-lg text-sm text-ink-muted">
            Verify ownership, select an approved target, confirm scope controls, and start a monitored test.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-faint">
            <span>
              {totalTargets} verified target{totalTargets === 1 ? '' : 's'}
            </span>
            {canReadKillSwitch && killSwitchState ? (
              <span className="inline-flex items-center gap-1">
                <Power size={12} className={killSwitchState.halted ? 'text-danger' : 'text-success'} />
                {killSwitchState.halted ? 'Kill switch active — testing halted' : 'Kill switch ready'}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {newScanButton}
          <Button asChild variant="outline">
            <Link to="/targets">
              <TargetIcon size={16} />
              View Targets
            </Link>
          </Button>
        </div>
      </div>

      <PageHeader
        title="Dashboard"
        description="Monitor security testing activity, exposure, and items requiring attention."
        action={
          <div className="flex items-center gap-2">
            <Select value={rangeDays} onValueChange={(v) => setRangeDays(v as '7' | '30' | '90')}>
              <SelectTrigger className="w-40" aria-label="Date range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportRecentScansCsv}>
              <Download size={16} />
              Export
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<TargetIcon size={20} />}
          label="Total targets"
          value={totalTargets}
          trend={newTargets > 0 ? { label: `+${newTargets} in last ${rangeDays}d`, tone: 'success' } : undefined}
        />
        <StatCard
          icon={<PlayCircle size={20} />}
          label="Active scans"
          value={activeScans}
          trend={scansToday > 0 ? { label: `${scansToday} started today`, tone: 'neutral' } : undefined}
        />
        <StatCard
          icon={<AlertOctagon size={20} />}
          label="Open findings (Crit/High)"
          value={openCritHigh}
          tone={findingsTone(openCritical, openHigh)}
          trend={newCritHigh > 0 ? { label: `+${newCritHigh} in last ${rangeDays}d`, tone: 'danger' } : { label: 'No new Crit/High', tone: 'success' }}
        />
        <StatCard
          icon={<CalendarClock size={20} />}
          label="Last scan"
          value={lastScan ? new Date(lastScan).toLocaleDateString() : '—'}
          trend={{ label: `${completedInRange} completed in last ${rangeDays}d`, tone: 'neutral' }}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>Findings by Severity</CardTitle>
            </CardHeader>
            <CardContent>
              <SeverityDonut counts={severityCounts} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Findings Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <FindingsTrendChart points={trendPoints} />
            </CardContent>
          </Card>
        </div>
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Top Attack Paths</CardTitle>
            </CardHeader>
            <CardContent>
              <TopAttackPathsPanel targets={rankedTargets} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Security Posture</CardTitle>
            </CardHeader>
            <CardContent>
              <SecurityPostureGauge posture={posture} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="mt-4">
        <CardContent className="pt-6">
          <h2 className="mb-4 text-sm font-semibold text-ink">Recent scans</h2>
          {recentScans.length === 0 ? (
            <EmptyState icon={<PlayCircle size={28} />} title="No scans yet" description="Start a scan to see it here." action={newScanButton} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-ink-faint">
                      <th className="pb-2 pr-4 font-medium">Target</th>
                      <th className="pb-2 pr-4 font-medium">Status</th>
                      <th className="pb-2 pr-4 font-medium">Started</th>
                      <th className="pb-2 pr-4 font-medium">Findings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentScans.slice(0, RECENT_SCANS_LIMIT).map(({ engagement, run }) => (
                      <tr key={run.id} className="border-b border-border transition-colors last:border-0 hover:bg-surface">
                        <td className="py-3 pr-4">
                          <Link to={`/runs/${run.id}`} className="font-medium text-ink hover:text-brand-hover hover:underline">
                            {engagement.name}
                          </Link>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant={RUN_STATUS_VARIANT[run.status]} className="capitalize">
                            {run.status}
                          </Badge>
                          {ACTIVE_RUN_STATUSES.includes(run.status) ? (
                            <div className="mt-1.5 h-1.5 w-24 overflow-hidden rounded-full bg-surface" role="progressbar" aria-valuenow={run.progressPct} aria-valuemin={0} aria-valuemax={100}>
                              <div className="h-full rounded-full bg-info" style={{ width: `${Math.min(100, Math.max(0, run.progressPct))}%` }} />
                            </div>
                          ) : null}
                        </td>
                        <td className="py-3 pr-4 text-ink-muted">{new Date(run.createdAt).toLocaleString()}</td>
                        <td className="py-3 pr-4 text-ink-muted">{findingCountByRunId.get(run.id) ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Link to="/scans" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-hover hover:underline">
                View all scans
                <ArrowRight size={14} />
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
